/**
 * ExternalWithdrawTable Component
 * 외부 출금 내역 테이블 (아코디언 없이 단순 표)
 */
import React, { useState } from 'react';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { ExternalWithdrawalDto, ExternalWithdrawalStatus } from '../types/headWithdraw.types';
import { formatAmount } from '../utils/amountFormatter';

const ITEMS_PER_PAGE = 10;

interface ExternalWithdrawTableProps {
    withdrawals: ExternalWithdrawalDto[];
    totalAmount: number;
}

const STATUS_LABEL: Record<ExternalWithdrawalStatus, string> = {
    ACCEPTED: '접수됨',
    PROCESSING: '처리 중',
    PENDING_CONFIRMATION: '확인 대기',
    SUCCEEDED: '성공',
    FAILED: '실패',
};

const STATUS_BADGE_CLASS: Record<ExternalWithdrawalStatus, string> = {
    ACCEPTED: 'badge-status-default',
    PROCESSING: 'badge-status-terminating',
    PENDING_CONFIRMATION: 'badge-status-terminating',
    SUCCEEDED: 'badge-status-operating',
    FAILED: 'badge-status-suspended',
};

const formatDateTime = (value?: string): string => {
    if (!value) return '-';
    // ISO or 'YYYY-MM-DDTHH:mm:ss' → 'YYYY-MM-DD HH:mm:ss'
    return value.replace('T', ' ').substring(0, 19);
};

const ExternalWithdrawTable: React.FC<ExternalWithdrawTableProps> = ({
    withdrawals,
    totalAmount,
}) => {
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(withdrawals.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentItems = withdrawals.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    if (withdrawals.length === 0) {
        return (
            <div className="empty-state">
                <p>조회된 외부 출금 내역이 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="external-withdraw-table-wrap">
            {/* 합계 */}
            <div className="external-withdraw-summary">
                <span className="summary-label">
                    <i className="fas fa-external-link-alt" />
                    외부 출금 합계
                </span>
                <span className="summary-amount withdrawal">
                    {formatAmount(totalAmount)}원
                </span>
                <span className="summary-count">
                    총 {withdrawals.length.toLocaleString()}건
                </span>
            </div>

            <div className="table-responsive">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th className="col-no">No</th>
                            <th className="col-datetime">신청일시</th>
                            <th className="col-amount">출금금액</th>
                            <th className="col-bank">은행</th>
                            <th className="col-account">계좌번호</th>
                            <th className="col-memo">적요</th>
                            <th className="col-status">상태</th>
                            <th className="col-datetime">완료일시</th>
                            <th className="col-result">결과메시지</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((item, idx) => {
                            const status = item.status as ExternalWithdrawalStatus;
                            return (
                                <tr key={item.seqNo}>
                                    <td className="col-no text-center">
                                        {startIndex + idx + 1}
                                    </td>
                                    <td className="col-datetime text-center">
                                        {formatDateTime(item.insDate)}
                                    </td>
                                    <td className="col-amount text-right withdrawal-amount">
                                        {formatAmount(item.transferAmount)}원
                                    </td>
                                    <td className="col-bank text-center">
                                        {item.bankCode}
                                    </td>
                                    <td className="col-account text-center">
                                        {item.accountNumber}
                                    </td>
                                    <td className="col-memo">
                                        {item.accountMemo || '-'}
                                    </td>
                                    <td className="col-status text-center">
                                        <span className={`badge-status ${STATUS_BADGE_CLASS[status] ?? 'badge-status-default'}`}>
                                            {STATUS_LABEL[status] ?? item.status}
                                        </span>
                                    </td>
                                    <td className="col-datetime text-center">
                                        {formatDateTime(item.completedAt)}
                                    </td>
                                    <td className="col-result">
                                        {item.resultMessage || '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="pagination-container">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    );
};

export default ExternalWithdrawTable;
